/**
 * ResetSection — Reset-to-Defaults confirmation dialog (extracted from the
 * old god component's `renderResetButton` + `handleReset`).
 */
import { useState } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useProductionSettings } from '../hooks/useProductionSettings';
import { SettingsSection } from '../components/console/SettingsSection';

export function ResetSection() {
  const { resetSettings } = useProductionSettings();
  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleReset = () => {
    resetSettings();
    setShowResetDialog(false);
    toast.success('Settings reset', { description: 'All settings have been restored to defaults' });
  };

  return (
    <SettingsSection
      id="reset"
      title="Reset to defaults"
      scope="browser"
      icon={RotateCcw}
      description="Restore this browser's production settings to their defaults. Does not affect org-wide or per-location settings."
    >
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-destructive gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset to Defaults
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore all production settings to their default values.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="h-11 gap-2">
              <Check className="h-4 w-4" aria-hidden="true" />
              Yes, Reset Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  );
}
